import { addFieldVersion, applyFieldVersion, removeFieldVersion, writeFieldVersion } from './versions.js';
import { coverageGaps } from './interval-coverage.js';
import { isNoSensorData, NO_SENSOR_DATA_TRIGGER } from './sensor-health.js';
import { LOOP_TRIGGER_TYPES, parseValueTrigger as parseValueTriggerShared } from './value-trigger.js';
import { overlappingAnswerPairs } from './answer-overlap';
import { answerHalfWidths } from './answer-tolerance.js';
import { optionalOutcome, SET_ANSWER_TRIGGER, participationOutcome } from './grading-mode.js';
import { magvar } from 'magvar';
import { resolveScenarioValueSetup } from './scenario.js';
/**
 * A compass admits only `exact` and `range`. Narrowing here rather than downstream keeps the return
 * type two-armed, so the type checker still proves every `switch` over a compass trigger is total —
 * which is the reason to pass the vocabulary in rather than accept the wide union and ignore it.
 *
 * There is no `lt` on a loop: "less than north" names no arc. A compass range with low > high is the
 * arc through the 0/360 seam, which is why compass keeps its own matching and merging too.
 */
function parseValueTrigger(trigger) {
    return parseValueTriggerShared(trigger, LOOP_TRIGGER_TYPES);
}
/**
 * Fixed accuracy-gate thresholds — deliberately NOT teacher-configurable, unlike GPS's own
 * accuracyCutoff. Both numbers are opaque to most teachers (what does "15 degrees of heading
 * uncertainty" mean to someone who's never used a compass block before?), and a mistyped value
 * has no natural upper bound to catch it — e.g. a teacher could type "154200" and silently make
 * the block unpassable for every student, with no feedback pointing at the real cause. The
 * `manualBypassEnabled` toggle is the actual escape hatch for a student stuck with a genuinely
 * poor sensor, not a tunable cutoff. Revisit only if there's real demand for authoring control
 * (see the project's own memory on this decision).
 */
export const COMPASS_HEADING_ACCURACY_CUTOFF = 15; // degrees
// Only ever consulted when northReference === 'true' (that's the only case needing a GPS fix,
// purely to look up magnetic declination) — much looser than GPS's own tighter default, since
// declination changes gently with position (well under a degree over tens of kilometers in most
// places); this isn't verifying *where* a student is standing the way GPS's own cutoff does.
export const COMPASS_POSITION_ACCURACY_CUTOFF = 5000; // meters
const EPS = (n) => 1e-9 * Math.max(1, Math.abs(n));
/**
 * Heading is a closed loop exactly like Angle's tilt domain — 0deg and 360deg are the same
 * point. No absolute reference ever "unwraps" a compass reading (unlike Angle's dip
 * measurement, which has gravity as an unambiguous flat=0 reference for the primary use case),
 * so this wraparound is *more* load-bearing here than for Angle: "face roughly north" puts the
 * accepted band astride the 0/360 seam in the ordinary case, not an edge case.
 */
const COMPASS_DOMAIN_LO = 0;
const COMPASS_DOMAIN_HI = 360;
/** Ported unchanged from rotation.ts's wrapRotationInterval — splits a tolerance band that may
 * overshoot [0, 360] into one or two real intervals within the domain, treating 0 and 360 as
 * the same point. E.g. 360 +/- 2 (naive [358, 362]) becomes [{358, 360}, {0, 2}]. */
function wrapCompassInterval(lo, hi) {
    const span = hi - lo;
    if (span >= 360)
        return [{ lo: COMPASS_DOMAIN_LO, hi: COMPASS_DOMAIN_HI }];
    let nlo = lo % 360;
    if (nlo < 0)
        nlo += 360;
    const nhi = nlo + span;
    if (nhi <= 360 + EPS(360))
        return [{ lo: nlo, hi: Math.min(nhi, COMPASS_DOMAIN_HI) }];
    return [{ lo: nlo, hi: COMPASS_DOMAIN_HI }, { lo: COMPASS_DOMAIN_LO, hi: nhi - 360 }];
}
/**
 * The accepted range(s) implied by the canonical condition, expressed as closed intervals
 * bounded to the [0, 360] domain. Multiple exact/between answers are OR'd together — matching
 * any one of them is correct. A 'between' answer with low > high (e.g. 340 to 30) is a
 * wraparound band through the 0/360 seam, same treatment as a targeted-feedback range trigger
 * (see rangeTriggerIntervals) — NOT a naive/empty range. This was a real, silent bug before the
 * fix: {lo:340, hi:30} taken literally makes `value >= 340 && value <= 30` impossible for any
 * value at all, so a between-answer authored this way could never actually match anything,
 * despite the editor showing it as if it were a valid accepted range.
 */
export function computeCompassAcceptedRanges(config) {
    const { conditionType, answers, betweenAnswers } = config;
    /*
     * An answer that did not RESOLVE contributes no accepted range at all.
     *
     * A half-typed answer row reaches here as NaN, and a NaN band is not merely useless — it is
     * actively corrosive. On a bounded domain `wrap(NaN, NaN)` came out as [NaN, 360] and [0, NaN],
     * two bogus spans that merged into the coverage and SWALLOWED a real gap: an answer of 90 +/- 5
     * with a blank second row reported Default Incorrect as covering only 0-85, losing 95-360
     * entirely. On an unbounded one it produced a gap running to NaN, printed as "greater than NaN".
     *
     * Dropping it at source rather than in each caller, because every consumer wants the same thing
     * and there are eighteen of them. Grading is unaffected: no submitted value ever satisfied a NaN
     * comparison, so these intervals only ever misinformed the EDITOR.
     */
    if (conditionType === 'exact') {
        return answers.filter(a => Number.isFinite(a.value)).flatMap(a => {
            const { below: loErr, above: hiErr } = answerHalfWidths(a, config);
            return wrapCompassInterval(a.value - loErr, a.value + hiErr);
        });
    }
    return betweenAnswers.filter(a => Number.isFinite(a.low) && Number.isFinite(a.high))
        .flatMap(a => rangeTriggerIntervals(a.low, a.high)); // 'between'
}
/** Is this value within the accepted condition? */
export function isCompassWithinAcceptedRange(value, config) {
    return computeCompassAcceptedRanges(config).some(r => value >= r.lo - EPS(r.lo) && value <= r.hi + EPS(r.hi));
}
/**
 * A 'range' trigger's own accepted interval(s) — reusing wrapCompassInterval rather than a
 * naive Math.min/max, so min > max is treated as a wraparound band (e.g. "310 to 50" sweeps
 * 310 -> 360/0 -> 50), not an inverted/empty one. On a circular domain there's no such thing as
 * a genuinely backwards range — every "min to max" pair describes exactly one clockwise sweep
 * from min to max, wrapping through 0/360 when max is numerically smaller. Degenerate case
 * (n1 === n2) collapses to a single point, same as an 'exact' trigger would.
 */
function rangeTriggerIntervals(n1, n2) {
    const span = ((n2 - n1) % 360 + 360) % 360;
    return wrapCompassInterval(n1, n1 + span);
}
function matchesValueTrigger(trigger, value) {
    const parsed = parseValueTrigger(trigger);
    if (!parsed)
        return false;
    const { type, n1, n2 } = parsed;
    switch (type) {
        case 'exact': return Math.abs(value - n1) < EPS(n1);
        case 'range': return rangeTriggerIntervals(n1, n2).some(iv => value >= iv.lo - EPS(iv.lo) && value <= iv.hi + EPS(iv.hi));
    }
}
/**
 * Returns the feedback states that match a student's compass capture.
 *
 * Evaluation sequence — the one genuine structural delta from evaluateRotation is the two-axis
 * accuracy gate (step 3/4), sitting in the same tier GPS's own accuracy gate does (checked
 * before survey mode, since a garbage/uncorrectable reading isn't real participation):
 * 1. optional mode -> any_response, touched or not, accurate or not — no gate or correctness
 *    check at all for this mode (see positiveTriggerForMode in use-input-block-feedback.ts).
 * 2. Untouched (graded/survey only) -> unanswered.
 * 3. Heading-accuracy gate — checked for SENSOR answers, regardless of northReference. A negative
 *    headingAccuracy means "no real signal reported" (mirrors iOS's own webkitCompassAccuracy
 *    convention) and never fires this gate on its own. Terminal poor_heading_accuracy state.
 * 4. IF northReference === 'true': position-accuracy gate, reusing GPS's own accuracy-radius
 *    shape — that's the only case needing a GPS fix at all, for the declination lookup.
 *    Missing positionAccuracy in True mode is treated as failing (never silently passes an
 *    ungated True-mode reading). Terminal poor_position_accuracy state.
 *
 *    Both accuracy gates are SKIPPED when method === 'manual'. They exist to reject a reading the
 *    instrument itself reports as untrustworthy, and a typed answer has no instrument behind it —
 *    the student reached for the bypass precisely because the sensor was missing, uncalibrated or
 *    hopeless. Applying them anyway would make the bypass unusable in exactly the situation it was
 *    added for, and in True-north mode it would fail every single time, since a typed answer
 *    carries no position and missing positionAccuracy is treated as failing.
 * 5. survey mode -> any_answer (any accurate-enough capture counts as participation).
 * 6. graded mode: same OR'd-answers/circular-wraparound logic as evaluateRotation, applied to
 *    headingDegrees.
 */
/**
 * The targeted states a READING fires outside graded: the value triggers it matches, plus Set
 * Answer when it lands inside the accepted band.
 *
 * Shared by survey and optional so the two cannot drift. Set Answer resolves against the band
 * derivation the correct card already uses, which is what keeps it in step with an edited answer
 * and makes it work with a formula answer.
 */
function participationMatches(reading, states, config) {
    return states.filter(st => st.type !== 'targeted' ? false
        : st.trigger === SET_ANSWER_TRIGGER ? isCompassWithinAcceptedRange(reading, config)
            : matchesValueTrigger(st.trigger, reading));
}
/**
 * This block's answer setup for the scenario in force — the value family's shared rule, with
 * only this block's own field list and fail-closed shape supplied.
 *
 * Empty answer lists are what "no row" means: nothing can be correct, which is the same
 * fail-closed choice the choice family makes by going all-neutral.
 */
export function resolveCompassScenario(d, scenarioId) {
    return resolveScenarioValueSetup(d, scenarioId, COMPASS_VERSION_KEYS, { answers: [], betweenAnswers: [] });
}
export function evaluateCompass(answer, health, rawBlockData, 
/** The leader's variant in force — see `resolveScenarioValueSetup`. */
scenarioId) {
    const blockData = resolveCompassScenario(rawBlockData, scenarioId);
    const { touched, headingDegrees } = answer;
    const { northReference, conditionType, answers, betweenAnswers, toleranceSymmetry, gradingMode, feedback, } = blockData;
    // `acceptedConfig` is hoisted here rather than declared with the graded matcher below,
    // because the survey branch reads it too and the later `rangeConfig` sits beneath that
    // branch. `readingIsMeasurable` went with optional: the gates own a missing reading now.
    const acceptedConfig = { conditionType, answers, betweenAnswers, toleranceSymmetry };
    /**
     * OPTIONAL fires targeted states too, which it did not.
     *
     * This returned `any_response` before the reading was looked at, so a value trigger authored
     * in optional could never fire and the editor listed chips that were dead. Survey has always
     * reached the matcher; optional was the odd one out.
     *
     * The GATES stay above it: no reading means nothing to match, which is a blank.
     */
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(feedback.states);
    }
    // The device failed with no way round it. ABOVE the blank check, because `touched` is false in
    // both cases and this is the more specific one: a student who could not measure is not a student
    // who did not try. Below `optional`, which short-circuits everything by design — a block nobody
    // is grading has no problem to route.
    if (isNoSensorData(health)) {
        const noData = feedback.states.find(s => s.type === 'default' && s.trigger === NO_SENSOR_DATA_TRIGGER);
        return noData ? [noData] : [];
    }
    if (!touched) {
        const unanswered = feedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    // Sensor answers only — see the accuracy note in this function's doc comment.
    if (answer.method === 'sensor') {
        // `?? -1` reads an ABSENT figure the same way a negative one reads: not reported, so the gate
        // cannot judge it. Optional now that this is the shared SensorHealth, and "unknown" and
        // "unreported" are the same fact.
        const heading = health.headingAccuracy ?? -1;
        if (heading >= 0 && heading > COMPASS_HEADING_ACCURACY_CUTOFF) {
            const poorHeading = feedback.states.find(s => s.type === 'default' && s.trigger === 'poor_heading_accuracy');
            return poorHeading ? [poorHeading] : [];
        }
        if (northReference === 'true') {
            if (health.positionAccuracy == null || health.positionAccuracy > COMPASS_POSITION_ACCURACY_CUTOFF) {
                const poorPosition = feedback.states.find(s => s.type === 'default' && s.trigger === 'poor_position_accuracy');
                return poorPosition ? [poorPosition] : [];
            }
        }
    }
    /** The targeted states this reading fires. ONE matcher, used by every grading mode — see
     *  `participationOutcome`. A reading is measurable whether or not any reading is the RIGHT
     *  one, so it still means something in a survey. */
    const targetedForReading = () => participationMatches(headingDegrees, feedback.states, acceptedConfig);
    // Targeted value states fire here too. Whether one CAN fire is analysed against the accepted
    // band, and outside graded there is no band in force — correct is never checked, so nothing is
    // subsumed by it. The editor's own analysis is mode-aware to match.
    if (gradingMode === 'survey') {
        return participationOutcome(feedback.states, 'survey', targetedForReading());
    }
    const rangeConfig = { conditionType, answers, betweenAnswers, toleranceSymmetry };
    if (isCompassWithinAcceptedRange(headingDegrees, rangeConfig)) {
        const correct = feedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
        return correct ? [correct] : [];
    }
    const matched = targetedForReading();
    if (matched.length > 0)
        return matched;
    const incorrect = feedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return incorrect ? [incorrect] : [];
}
// ─── Authoring-time reachability analysis ──────────────────────────────────────
// Direct ports of rotation.ts's mergeCoverageIntervals / isRotationDefaultIncorrectUnreachable /
// computeRotationDefaultIncorrectGaps / isIntervalFullyCovered / isRotationFeedbackFullySubsumed /
// rangesOverlap / doesRotationFeedbackOverlapAccepted / findRotationOverlappingFeedbackPairs,
// renamed Compass*, minus the one-sided (lt/lte/gt/gte) branches — not offered for Compass.
function mergeCoverageIntervals(acceptedRanges, feedbackTriggers) {
    const intervals = [...acceptedRanges];
    for (const trigger of feedbackTriggers) {
        const parsed = parseValueTrigger(trigger);
        // 'exact' contributes no real interval coverage — a single point can't close a real gap.
        // A wrapped 'range' (min > max) contributes its two wrapped pieces, same as an exact
        // answer's own tolerance band would.
        if (parsed?.type === 'range')
            intervals.push(...rangeTriggerIntervals(parsed.n1, parsed.n2));
    }
    const sorted = [...intervals].sort((a, b) => a.lo - b.lo);
    const merged = [];
    for (const iv of sorted) {
        const last = merged[merged.length - 1];
        if (last && iv.lo <= last.hi + EPS(last.hi))
            last.hi = Math.max(last.hi, iv.hi);
        else
            merged.push({ ...iv });
    }
    return merged;
}
/**
 * Whether Default Incorrect is genuinely unreachable — every point on the [0, 360] loop is
 * covered by either the accepted condition(s) or some value feedback. Defined directly in
 * terms of computeCompassDefaultIncorrectGaps (rather than checking for a single merged
 * interval spanning the whole domain) because a wrapped exact answer's coverage can legitimately
 * land as two merged pieces (one touching 0, one touching 360) that are contiguous around the
 * seam — "no gaps" is the correct test once wraparound is in play.
 */
export function isCompassDefaultIncorrectUnreachable(acceptedRanges, feedbackTriggers) {
    return computeCompassDefaultIncorrectGaps(acceptedRanges, feedbackTriggers).length === 0;
}
/**
 * The gaps that actually reach Default Incorrect — the [0, 360] loop's complement of the
 * accepted range(s) plus any value feedback coverage. Bounded to the domain's own edges.
 */
export function computeCompassDefaultIncorrectGaps(acceptedRanges, feedbackTriggers) {
    const merged = mergeCoverageIntervals(acceptedRanges, feedbackTriggers);
    /*
     * Closed flags IN, flags carried out.
     *
     * Every compass condition is closed — the block offers only `exact` and `range`, so no trigger
     * has an open bound, which is what the old note here said. True of the TRIGGERS, and beside the
     * point for the GAPS: a target closed at 40° leaves the leftover below it stopping just short of
     * 40°, and dropping the flags printed that as "N (0°) to NE (40°)" — two cards claiming 40°.
     */
    const closed = merged.map(m => ({ ...m, loInclusive: true, hiInclusive: true }));
    return coverageGaps(closed, COMPASS_DOMAIN_LO, COMPASS_DOMAIN_HI, EPS);
}
/** Whether `target` falls entirely within the union of `coverage` intervals (merging any that overlap or touch first). */
function isIntervalFullyCovered(target, coverage) {
    if (coverage.length === 0)
        return false;
    const sorted = [...coverage].sort((a, b) => a.lo - b.lo);
    const merged = [];
    for (const iv of sorted) {
        const last = merged[merged.length - 1];
        if (last && iv.lo <= last.hi)
            last.hi = Math.max(last.hi, iv.hi);
        else
            merged.push({ ...iv });
    }
    return merged.some(m => m.lo <= target.lo && target.hi <= m.hi);
}
/**
 * Whether a targeted feedback trigger's *entire* condition now falls inside the accepted
 * range(s) — i.e. it can never actually fire, since Correct is checked first and is terminal.
 * A wrapped 'range' trigger has two target pieces (see rangeTriggerIntervals); both must be
 * covered for the trigger as a whole to be subsumed.
 */
export function isCompassFeedbackFullySubsumed(trigger, acceptedRanges) {
    const parsed = parseValueTrigger(trigger);
    if (!parsed)
        return false;
    const { type, n1, n2 } = parsed;
    const targets = type === 'exact' ? [{ lo: n1, hi: n1 }] : rangeTriggerIntervals(n1, n2);
    return targets.every(target => isIntervalFullyCovered(target, acceptedRanges));
}
/**
 * Whether a targeted feedback trigger's target interval intersects the accepted Correct
 * range(s) at all — a strictly weaker condition than isCompassFeedbackFullySubsumed. Both ends
 * of a 'range' trigger and of an accepted range are always closed/inclusive (no lt/gt-style
 * exclusive boundary exists for Compass), so a shared boundary point is always a real overlap.
 * A wrapped 'range' trigger overlaps if *either* of its two pieces does.
 */
export function doesCompassFeedbackOverlapAccepted(trigger, acceptedRanges) {
    const parsed = parseValueTrigger(trigger);
    if (!parsed || parsed.type === 'exact')
        return false;
    const targets = rangeTriggerIntervals(parsed.n1, parsed.n2);
    return targets.some(({ lo, hi }) => acceptedRanges.some(r => hi >= r.lo && r.hi >= lo));
}
/**
 * Pairs of ACCEPTED ANSWERS covering the same headings.
 *
 * The same check Angle carries, for the same reason: a compass rose is a closed circle, so "0 +/- 2"
 * and "360 +/- 2" are one answer written two ways, and a numeric duplicate check sees them 360 apart
 * and says nothing. Running the comparison on the intervals AFTER wrapCompassInterval has split them
 * makes the seam case fall out with no special case — both spellings resolve to [358,360] and [0,2].
 *
 * Returns answer ids so a caller can point at the offending rows. A warning, never an error:
 * acceptance is their union, so grading is right either way. The authoring is what is wrong.
 *
 * GPS has no equivalent and needs none — one target plus one tolerance is a single range by
 * construction, with nothing to collide against.
 */
export function findCompassOverlappingAnswerPairs(config) {
    const { conditionType, answers, betweenAnswers } = config;
    const perAnswer = conditionType === 'exact'
        ? answers.map(a => {
            const { below: loErr, above: hiErr } = answerHalfWidths(a, config);
            return { id: a.id, intervals: wrapCompassInterval(a.value - loErr, a.value + hiErr) };
        })
        : conditionType === 'between'
            ? betweenAnswers.map(a => ({ id: a.id, intervals: wrapCompassInterval(a.low, a.high) }))
            : [];
    // Comparison is shared — see `overlappingAnswerPairs`. Only the interval BUILDING above is
    // this block's own, because only this block knows how its domain behaves.
    return overlappingAnswerPairs(perAnswer);
}
/**
 * Pairs of currently-authored targeted feedback triggers whose ranges overlap each other —
 * likely unintentional. 'exact' triggers are excluded, mirroring numerical.ts's/rotation.ts's own
 * findOverlappingFeedbackPairs. A wrapped 'range' trigger contributes both of its pieces, each
 * tagged with the same trigger id; a trigger's own two wrapped pieces are never compared against
 * each other (that's not a cross-trigger overlap), and each pair is only ever reported once even
 * if both wrapped pieces of one trigger separately overlap the other.
 */
export function findCompassOverlappingFeedbackPairs(feedbackTriggers) {
    const parsed = feedbackTriggers.flatMap(trigger => {
        const p = parseValueTrigger(trigger);
        if (!p || p.type === 'exact')
            return [];
        return rangeTriggerIntervals(p.n1, p.n2).map(iv => ({ trigger, lo: iv.lo, hi: iv.hi }));
    });
    const seenPairs = new Set();
    const pairs = [];
    for (let i = 0; i < parsed.length; i++) {
        for (let j = i + 1; j < parsed.length; j++) {
            const a = parsed[i], b = parsed[j];
            if (a.trigger === b.trigger)
                continue;
            if (a.hi >= b.lo && b.hi >= a.lo) {
                const key = [a.trigger, b.trigger].sort().join(' ');
                if (!seenPairs.has(key)) {
                    seenPairs.add(key);
                    pairs.push([a.trigger, b.trigger]);
                }
            }
        }
    }
    return pairs;
}
// ─── Magnetic -> True north correction ─────────────────────────────
function normalizeHeading(deg) {
    const m = deg % 360;
    return m < 0 ? m + 360 : m;
}
/**
 * Resolves a magnetic-compass reading to a true-north heading via the WMM declination model
 * (the `magvar` package), normalized back into [0, 360). Lives here — not in teacher-interface
 * — so both the teacher-editor's capture-to-author flow and the future student-interface
 * runner call the same correction rather than each hand-rolling it, mirroring
 * haversineDistanceMeters's placement in gps.ts for the identical reason. evaluateCompass
 * itself never calls this — see CompassAnswer's own doc comment; the correction must already
 * have happened upstream of whatever headingDegrees gets submitted.
 *
 * magvar's declination() computes for "now" internally (captured once at module load, not
 * per-call) rather than accepting an arbitrary date — entirely adequate here, since every
 * caller is resolving a reading taken in the same session it's being computed in, and
 * declination drifts by a small fraction of a degree per year. Altitude is intentionally
 * omitted (defaults to 0km in magvar) — its effect on declination is negligible for any
 * near-surface field-education use case.
 */
export function computeTrueHeading(magneticHeadingDegrees, lat, lng) {
    const declinationDegrees = magvar(lat, lng);
    return normalizeHeading(magneticHeadingDegrees + declinationDegrees);
}
const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
export function cardinalPointFromDegrees(deg) {
    const normalized = ((deg % 360) + 360) % 360;
    return COMPASS_POINTS[Math.round(normalized / 45) % 8];
}
/**
 * The fields a Compass version owns — §3.2 of `docs/question-versions.md`.
 *
 * `northReference` and `liveDisplayFormat` stay shared: they say how a bearing is read and
 * written, not which bearing is right.
 */
export const COMPASS_VERSION_KEYS = ['conditionType', 'answers', 'toleranceSymmetry', 'betweenAnswers'];
export function applyCompassVersion(data, versionId) {
    return applyFieldVersion(data, versionId, COMPASS_VERSION_KEYS);
}
export function writeCompassVersion(current, versionId, next) {
    return writeFieldVersion(current, versionId, next, COMPASS_VERSION_KEYS);
}
export function addCompassVersion(data, fromVersionId, newId) {
    return addFieldVersion(data, fromVersionId, newId, COMPASS_VERSION_KEYS);
}
export function removeCompassVersion(data, versionId) {
    return removeFieldVersion(data, versionId, COMPASS_VERSION_KEYS);
}
//# sourceMappingURL=compass.js.map